import { Autocomplete, ChipProps as ChipPropsType, TextField } from '@mui/material';
import {
  CuriosityTranslationOrKey,
  CuriosityAutocompleteProps,
  useValueSimplify,
  useCuriosityAutocomplete,
  CuriosityNamespaceType,
  isCuriosityTranslation,
} from '@procureai/curiosity-ui-mui';
import { identity, indexBy } from 'ramda';
import { ReactNode, ForwardedRef, forwardRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SupplierAutocompleteHit, SupplierEntity } from './SupplierAutocomplete.types';
import { SupplierOption, SupplierOptionProps } from './SupplierOption/SupplierOption';

const ChipProps: ChipPropsType = { variant: 'outlined' };

type FreeSolo = false;

export interface SupplierAutocompleteProps<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
> extends Omit<CuriosityAutocompleteProps<SupplierEntity, Multiple, DisableClearable>, 'ns'> {
  renderOptionActions?: (item: SupplierAutocompleteHit, close: () => void) => ReactNode;
  filterOptionsByKey?: string[];
}

const SupplierAutocompleteInner = <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
>(
  {
    TextFieldProps,
    onChange: onChangeProp,
    value: valueProp,
    size = 'small',
    multiple,
    simplifyValue,
    renderOptionActions,
    filterOptionsByKey,
    ...props
  }: SupplierAutocompleteProps<Multiple, DisableClearable>,
  ref: ForwardedRef<unknown>,
) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation(undefined, { keyPrefix: 'Autocomplete' });
  const { value, onChange } = useValueSimplify({ simplifyValue, onChange: onChangeProp, value: valueProp, multiple });
  const autocompleteProps = useCuriosityAutocomplete<SupplierEntity, Multiple, DisableClearable>({
    ns: CuriosityNamespaceType.SUPPLIER,
    multiple,
    value,
    onChange,
  });

  const filterOptionsByKeyMap = useMemo(
    () => (filterOptionsByKey && filterOptionsByKey.length ? indexBy(identity, filterOptionsByKey) : undefined),
    [filterOptionsByKey],
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Autocomplete<CuriosityTranslationOrKey<SupplierEntity>, Multiple, DisableClearable, FreeSolo>
      ref={ref}
      size={size}
      openOnFocus
      blurOnSelect
      filterOptions={(options) => {
        if (filterOptionsByKeyMap) {
          return options.filter((option) => {
            const key = isCuriosityTranslation(option) ? option.key : option;
            return !filterOptionsByKeyMap[key];
          });
        }
        return options;
      }}
      filterSelectedOptions
      noOptionsText={t('noOptions')}
      loadingText={t('loading')}
      renderInput={(params) => (
        <TextField
          {...params}
          {...TextFieldProps}
          InputProps={{ ...params.InputProps, ...TextFieldProps?.InputProps }}
        />
      )}
      renderOption={(params, option) => {
        const renderActions: SupplierOptionProps['renderActions'] =
          renderOptionActions &&
          ((data) => {
            return renderOptionActions(data, handleClose);
          });
        return (
          <SupplierOption {...params} data={option as any} renderActions={renderActions} key={(option as any).key} />
        );
      }}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      {...props}
      {...autocompleteProps}
      slotProps={{
        chip: ChipProps,
      }}
    />
  );
};

export const SupplierAutocomplete = forwardRef(SupplierAutocompleteInner) as <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
>(
  props: SupplierAutocompleteProps<Multiple, DisableClearable> & {
    ref?: ForwardedRef<unknown>;
  },
) => ReturnType<typeof SupplierAutocompleteInner>;
